// SOW register table. Six columns per brief §5 layer 3a:
//   Name | Number | Period | Value | Status | Actions
//
// Renders via the shared TrackingTable shell so the inline-expand pattern,
// keyboard semantics, and deep-link highlight ring all match the other
// tracking surfaces. Per-row inline expand renders SowInlineExpand.
//
// The Value column shows the current value — the original value plus
// approved variation contributions, derived by `currentSowValue` (the
// same helper the expanded detail view uses, so table and detail never
// disagree). Each value carries an
// `ex GST` (or basis-equivalent) suffix per the GST labelling rule.

import { Pencil, Archive, CheckCircle2, XCircle } from "lucide-react";
import TrackingTable, {
  type Column,
} from "../shared/TrackingTable";
import SowDateCell from "./SowDateCell";
import SowStatusPill from "./SowStatusPill";
import { formatSowCurrency } from "./formatSowCurrency";
import { currentSowValue } from "./contractValueDerivation";
import CopyableIdentifier from "../../../common/CopyableIdentifier";
import type { Sow, Variation } from "../../../../types";
import type { ReactNode } from "react";

export type SowAction = "activate" | "cancel" | "edit" | "archive";

export interface SowTableProps {
  items: Sow[];
  isLoading: boolean;
  error: unknown;
  gstApplies: boolean | undefined;
  /** Variations on this contract — the Value column derives each SOW's
   *  current value from approved variation contributions. Defaults to
   *  empty so the column falls back to the original value when the
   *  variation feed is not wired. */
  variations?: Variation[];
  onRowClick?: (item: Sow) => void;
  onAction?: (item: Sow, action: SowAction) => void;
  highlightRowId?: string | null;
  expandedRowId?: string | null;
  renderExpandedRow?: (item: Sow) => ReactNode;
  emptyMessage?: string;
}

export default function SowTable({
  items,
  isLoading,
  error,
  gstApplies,
  variations = [],
  onRowClick,
  onAction,
  highlightRowId,
  expandedRowId,
  renderExpandedRow,
  emptyMessage = "No SOWs recorded yet.",
}: SowTableProps) {
  const columns: Column<Sow>[] = [
    {
      header: "Name",
      width: "32%",
      clickable: true,
      cell: (item) => (
        <div className="flex flex-col">
          <span className="text-body text-slate-900 dark:text-slate-100 font-medium">
            {item.name}
          </span>
          {item.description && (
            <span className="text-caption text-slate-500 dark:text-slate-400 line-clamp-1">
              {item.description}
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Number",
      width: "13%",
      cell: (item) => (
        <CopyableIdentifier value={item.number} label={`SOW number ${item.number}`} />
      ),
    },
    {
      header: "Period",
      width: "21%",
      cell: (item) => (
        <SowDateCell
          startDate={item.startDate}
          endDate={item.endDate}
          status={item.status}
        />
      ),
    },
    {
      header: "Value",
      width: "12%",
      align: "right",
      cellClassName: "tabular-nums",
      cell: (item) => {
        const formatted = formatSowCurrency(
          currentSowValue(item, variations),
          gstApplies,
        );
        return (
          <div className="flex flex-col items-end">
            <span
              className="text-body text-slate-900 dark:text-slate-100"
              data-testid={`sow-row-${item.id}-value`}
            >
              {formatted.value}
            </span>
            <span
              className="text-caption text-slate-500 dark:text-slate-400"
              data-testid={`sow-row-${item.id}-basis`}
            >
              {formatted.suffix}
            </span>
          </div>
        );
      },
    },
    {
      header: "Status",
      width: "12%",
      cell: (item) => <SowStatusPill status={item.status} />,
    },
    {
      header: "Actions",
      width: "14%",
      align: "right",
      cell: (item) => {
        const isDraft = item.status === "draft";
        return (
          <div className="inline-flex gap-1 justify-end">
            {isDraft && (
              <>
                <button
                  type="button"
                  aria-label={`Activate ${item.name}`}
                  data-testid={`sow-row-${item.id}-activate`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction?.(item, "activate");
                  }}
                  className="inline-flex items-center justify-center w-8 h-8 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label={`Cancel ${item.name}`}
                  data-testid={`sow-row-${item.id}-cancel`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction?.(item, "cancel");
                  }}
                  className="inline-flex items-center justify-center w-8 h-8 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                  <XCircle className="h-4 w-4" aria-hidden="true" />
                </button>
              </>
            )}
            <button
              type="button"
              aria-label={`Edit ${item.name}`}
              data-testid={`sow-row-${item.id}-edit`}
              onClick={(e) => {
                e.stopPropagation();
                onAction?.(item, "edit");
              }}
              className="inline-flex items-center justify-center w-8 h-8 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label={`Archive ${item.name}`}
              data-testid={`sow-row-${item.id}-archive`}
              onClick={(e) => {
                e.stopPropagation();
                onAction?.(item, "archive");
              }}
              className="inline-flex items-center justify-center w-8 h-8 text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
            >
              <Archive className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <TrackingTable
      columns={columns}
      items={items}
      isLoading={isLoading}
      error={error}
      emptyMessage={emptyMessage}
      rowKey={(item) => item.id}
      onRowClick={onRowClick}
      highlightRowId={highlightRowId}
      expandedRowId={expandedRowId}
      renderExpandedRow={renderExpandedRow}
    />
  );
}
